import type {
  Announcement,
  AboutPageSettings,
  DocumentItem,
  NewsItem,
  SchoolInfo,
  SiteConfig,
  AlbumItem,
  LearningResourceItem,
  PageContent,
  ProgramItem,
  SearchResult,
  TrainingPageSettings,
  EventItem,
} from '../types/content'

const CMS_URL = import.meta.env.VITE_CMS_URL || 'http://localhost:3001'
const BASE = import.meta.env.VITE_API_BASE_URL || `${CMS_URL}/api/public/v1`
const SITE_SLUG = import.meta.env.VITE_SITE_SLUG || 'thcs-ly-thuong-kiet'

const apiURL = (path: string) => {
  const url = new URL(`${BASE}${path}`)
  url.searchParams.set('site', SITE_SLUG)
  return url.toString()
}

const jsonHeaders = (headers?: HeadersInit): HeadersInit => ({
  'Content-Type': 'application/json',
  ...(headers || {}),
})

// Response shape từ backend: { success, data, meta, error }
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiURL(path), {
    ...init,
    headers: jsonHeaders(init?.headers),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err?.error?.message || `API ${response.status}: ${response.statusText}`)
  }
  const result = await response.json()
  // Backend trả { success, data, meta, error }
  if (result && 'data' in result) return result.data as T
  return result as T
}

// ---------- Types cho response pagination ----------
export type PaginatedResponse<T> = {
  data: T[]
  meta: { page: number; total: number; totalPages: number; limit: number }
}

async function requestPaginated<T>(path: string, init?: RequestInit): Promise<PaginatedResponse<T>> {
  const response = await fetch(apiURL(path), {
    ...init,
    headers: jsonHeaders(init?.headers),
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err?.error?.message || `API ${response.status}: ${response.statusText}`)
  }
  const result = await response.json()
  return { data: result.data ?? [], meta: result.meta ?? {} } as PaginatedResponse<T>
}

// ---------- API ----------
export const api = {
  // Trang chủ: news, announcements, documents, school info, homepage slides, albums
  async getHome() {
    return request<{
      school: SchoolInfo
      homepage: {
        slides: Array<{ title: string; subtitle?: string; imageUrl: string; buttonLabel?: string; buttonUrl?: string }>
        quickLinks?: Array<{ label: string; url: string; icon?: string }>
        statistics?: Array<{ label: string; value: string; icon?: string }>
        partnerLinks?: Array<{ name: string; url: string; logo?: string }>
      }
      news: NewsItem[]
      announcements: Announcement[]
      documents: DocumentItem[]
      albums: AlbumItem[]
      events: EventItem[]
    }>('/home')
  },

  // Danh sách bài viết
  async getPosts(category?: string, page = 1, limit = 12) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (category && category !== 'Tất cả') params.set('category', category)
    return requestPaginated<NewsItem>(`/posts?${params}`)
  },

  // Chi tiết bài viết theo slug
  async getPost(slug: string): Promise<NewsItem | undefined> {
    try {
      return await request<NewsItem>(`/posts/${slug}`)
    } catch {
      return undefined
    }
  },

  // Thông báo
  async getAnnouncements(page = 1, limit = 20) {
    return requestPaginated<Announcement>(`/announcements?page=${page}&limit=${limit}`)
  },

  async getAnnouncement(slug: string): Promise<Announcement | undefined> {
    try {
      return await request<Announcement>(`/announcements/${slug}`)
    } catch {
      return undefined
    }
  },

  // Lịch công tác / sự kiện
  async getEvents(page = 1, limit = 20, eventType = 'all') {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) })
    if (eventType !== 'all') params.set('eventType', eventType)
    return requestPaginated<EventItem>(`/events?${params}`)
  },

  async getEvent(slug: string): Promise<EventItem | undefined> {
    try {
      return await request<EventItem>(`/events/${slug}`)
    } catch {
      return undefined
    }
  },

  // Văn bản - tài liệu
  async getDocuments(page = 1, limit = 20) {
    return requestPaginated<DocumentItem>(`/documents?page=${page}&limit=${limit}`)
  },

  async getAlbums() {
    return request<AlbumItem[]>('/albums')
  },

  async getLearningResources(page = 1, limit = 20) {
    return requestPaginated<LearningResourceItem>(`/resources?page=${page}&limit=${limit}`)
  },

  async getPage(slug: string): Promise<PageContent | undefined> {
    try {
      return await request<PageContent>(`/pages/${slug}`)
    } catch {
      return undefined
    }
  },

  // Chương trình đào tạo
  async getPrograms() {
    return request<ProgramItem[]>('/programs')
  },

  async getProgram(slug: string): Promise<ProgramItem | undefined> {
    try {
      const programs = await request<ProgramItem[]>('/programs')
      return programs.find((program) => program.slug === slug || program.id === slug)
    } catch {
      return undefined
    }
  },

  // Nội dung trang đào tạo
  async getTrainingPage() {
    return request<TrainingPageSettings>('/training-page')
  },

  async getAboutPage() {
    return request<AboutPageSettings>('/about-page')
  },

  // Cấu hình website (site settings, header, footer, social...)
  async getSiteConfig(): Promise<SiteConfig> {
    return request<SiteConfig>('/site-config')
  },

  // Tìm kiếm
  async search(q: string): Promise<SearchResult[]> {
    if (!q.trim()) return []
    return request<SearchResult[]>(`/search?q=${encodeURIComponent(q)}`)
  },

  // Gửi liên hệ
  async submitContact(data: { fullName: string; email: string; phone?: string; message: string }) {
    return request<{ success: boolean; id?: string }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}
